import type { CSSProperties } from 'react'
import type { Employee } from '@lms/shared-types'
import { displayName } from '../../lib/display-name'

export interface RecipientGroup {
  /** Human-readable group heading (e.g. 'From team "Platform"', 'Direct-add'). */
  label: string
  members: ReadonlyArray<Employee>
}

export interface RecipientChipsProps {
  groups: ReadonlyArray<RecipientGroup>
  /** Invoked with the employee id when the user clicks × on a chip. */
  onRemove: (employeeId: string) => void
}

const wrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}

const groupHeaderStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-3)',
  margin: '0 0 4px',
}

const chipsRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
}

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px 4px 10px',
  borderRadius: 999,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  fontSize: 12,
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
}

const xButtonStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  color: 'var(--text-3)',
  padding: 2,
}

/**
 * Grouped display of selected recipients. Each chip carries an × button that
 * calls onRemove(employee.id). Returns null when there are no groups so a
 * caller can render this unconditionally without an empty wrapper appearing.
 */
export function RecipientChips({
  groups,
  onRemove,
}: Readonly<RecipientChipsProps>): JSX.Element | null {
  if (groups.length === 0) return null
  return (
    <div style={wrapStyle}>
      {groups.map((g) => (
        <div key={g.label}>
          <p style={groupHeaderStyle}>{g.label}</p>
          <div style={chipsRowStyle}>
            {g.members.map((m) => (
              <span key={m.id} style={chipStyle}>
                {displayName(m)}
                <button
                  type="button"
                  onClick={() => onRemove(m.id)}
                  aria-label={`remove ${displayName(m)}`}
                  style={xButtonStyle}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
